import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ThumbsUp, HelpCircle, ThumbsDown, CheckCircle, Landmark } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const TEXTS = {
  de: {
    heading: 'AC-Meeting Feedback',
    dealLabel: 'Ihr Feedback zum Deal',
    meeting: 'AC-Meeting am',
    votes: { interested: 'Interessant', questions: 'Fragen', decline: 'Nicht interessant' },
    commentLabel: 'Kommentar (optional)',
    commentPlaceholder: 'Ihre Anmerkungen, Fragen oder Bedenken zu diesem Deal…',
    submit: 'Feedback senden',
    update: 'Feedback aktualisieren',
    existingHint: 'Sie haben bereits Feedback abgegeben — Absenden aktualisiert es.',
    successTitle: 'Vielen Dank!',
    successBody: 'Ihr Feedback wurde gespeichert und dem OLTA-Team mitgeteilt.',
    successEdit: 'Feedback ändern',
    expired: 'Dieser Feedback-Link ist abgelaufen — Links sind bis zum Tag des AC-Meetings gültig.',
    invalid: 'Dieser Feedback-Link ist ungültig.',
    needInput: 'Bitte wählen Sie eine Bewertung oder schreiben Sie einen Kommentar.',
    error: 'Speichern fehlgeschlagen — bitte versuchen Sie es erneut.',
  },
  en: {
    heading: 'AC Meeting Feedback',
    dealLabel: 'Your feedback on the deal',
    meeting: 'AC meeting on',
    votes: { interested: 'Interested', questions: 'Questions', decline: 'Not interested' },
    commentLabel: 'Comment (optional)',
    commentPlaceholder: 'Your notes, questions or concerns about this deal…',
    submit: 'Send feedback',
    update: 'Update feedback',
    existingHint: 'You already gave feedback — submitting will update it.',
    successTitle: 'Thank you!',
    successBody: 'Your feedback has been saved and shared with the OLTA team.',
    successEdit: 'Edit feedback',
    expired: 'This feedback link has expired — links are valid until the day of the AC meeting.',
    invalid: 'This feedback link is invalid.',
    needInput: 'Please pick a vote or write a comment.',
    error: 'Saving failed — please try again.',
  },
};

const VOTE_META = [
  { key: 'interested', icon: ThumbsUp, active: 'bg-emerald-500/15 border-emerald-500 text-emerald-300' },
  { key: 'questions', icon: HelpCircle, active: 'bg-blue-500/15 border-blue-500 text-blue-300' },
  { key: 'decline', icon: ThumbsDown, active: 'bg-red-500/15 border-red-500 text-red-300' },
];

const fmtDate = (s, lang) => {
  if (!s) return '';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'de-DE',
    { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function ACFeedbackPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const urlVote = params.get('vote');
  const [state, setState] = useState('loading');
  const [ctx, setCtx] = useState(null);
  const [vote, setVote] = useState(null);
  const [comment, setComment] = useState('');
  const [hadExisting, setHadExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    axios.get(`${API}/api/public/ac-feedback/context`, { params: { token }, withCredentials: false })
      .then(res => {
        setCtx(res.data);
        const valid = ['interested', 'questions', 'decline'];
        setVote(valid.includes(urlVote) ? urlVote : (res.data.vote || null));
        setComment(res.data.comment || '');
        setHadExisting(!!res.data.vote || !!res.data.comment);
        setState('form');
      })
      .catch(err => setState(err.response?.status === 410 ? 'expired' : 'invalid'));
  }, [token, urlVote]);

  const t = TEXTS[ctx?.lang === 'en' ? 'en' : 'de'];

  const submit = async () => {
    if (!vote && !comment.trim()) { setErrMsg(t.needInput); return; }
    setSaving(true); setErrMsg('');
    try {
      await axios.post(`${API}/api/public/ac-feedback`, { token, vote, comment: comment.trim() },
        { withCredentials: false });
      setState('success');
      setHadExisting(true);
    } catch (err) {
      if (err.response?.status === 410) setState('expired');
      else setErrMsg(err.response?.data?.detail || t.error);
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4" data-testid="ac-feedback-page">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="w-5 h-5 text-purple-400" />
          <p className="font-heading text-lg">OLTA Nachfolge — {t.heading}</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-6">
          {state === 'loading' && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {(state === 'expired' || state === 'invalid') && (
            <p className="text-sm text-amber-400 text-center py-6" data-testid="ac-feedback-error">
              {state === 'expired' ? t.expired : t.invalid}
            </p>
          )}
          {state === 'success' && (
            <div className="text-center py-6" data-testid="ac-feedback-success">
              <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="font-heading text-lg">{t.successTitle}</p>
              <p className="text-sm text-muted-foreground mt-1">{t.successBody}</p>
              <Button variant="outline" onClick={() => setState('form')} data-testid="ac-feedback-edit-btn"
                className="mt-5 border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-8">
                {t.successEdit}
              </Button>
            </div>
          )}
          {state === 'form' && ctx && (
            <div className="space-y-5">
              <div>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{t.dealLabel}</p>
                <p className="font-heading text-xl mt-0.5" data-testid="ac-feedback-deal-name">{ctx.project_name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t.meeting} <span className="text-purple-300">{fmtDate(ctx.meeting_date, ctx.lang)}</span> · {ctx.email}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {VOTE_META.map(({ key, icon: Icon, active }) => (
                  <button key={key} onClick={() => setVote(vote === key ? null : key)}
                    data-testid={`ac-vote-${key}`}
                    className={`flex flex-col items-center gap-1.5 py-3 border rounded-sm text-sm font-medium transition-colors ${
                      vote === key ? active : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'}`}>
                    <Icon className="w-5 h-5" /> {t.votes[key]}
                  </button>
                ))}
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1.5">{t.commentLabel}</p>
                <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4}
                  placeholder={t.commentPlaceholder} data-testid="ac-feedback-comment"
                  className="w-full bg-secondary border border-border rounded-sm p-3 text-sm text-foreground resize-y focus:outline-none focus:border-purple-500" />
              </div>
              {hadExisting && <p className="text-[11px] text-amber-400">{t.existingHint}</p>}
              {errMsg && <p className="text-xs text-red-400" data-testid="ac-feedback-form-error">{errMsg}</p>}
              <Button onClick={submit} disabled={saving} data-testid="ac-feedback-submit"
                className="w-full bg-purple-600 hover:bg-purple-500 text-white rounded-sm h-10">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {hadExisting ? t.update : t.submit}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
